import * as React from 'react';

import { CraftUsersSessionInfoResponse, SurveyResultsResponse } from '../types';

const fetcher = async (
  url: string,
  submitterUid: string,
  answerUid: string
) => {
  const csrfData: CraftUsersSessionInfoResponse = await fetch(
    '/actions/users/session-info',
    {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }
  ).then((res) => res.json());

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'BVMW-Survey-Submitter-UID': submitterUid,
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfData.csrfTokenValue,
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({
      answerUid
    })
  }).then((res) => res.json());
};

const useQuestionResult = (
  submitterUid: string,
  surveyUid: string,
  questionUid: string
) => {
  /**
   * Response data to the submitted answer, i.e. survey results
   */
  const [data, setData] = React.useState<SurveyResultsResponse | null>(null);

  /**
   * Whether an answer request is currently running
   */
  const [isLoading, setIsLoading] = React.useState(false);

  /**
   * Submit an answer to a question
   *
   * @param answerUid UID of the selected answer
   * @returns A promise that resolves when the request has finished
   */
  const submitAnswer = React.useCallback(
    async (answerUid: string) => {
      setIsLoading(true);

      const url = `/api/surveys/${surveyUid}/questions/${questionUid}/submissions`;

      try {
        const resData: SurveyResultsResponse = await fetcher(
          url,
          submitterUid,
          answerUid
        );

        setData(resData);
      } catch (e) {
        // pass
      }

      setIsLoading(false);
    },
    [surveyUid, questionUid, submitterUid, setIsLoading, setData]
  );

  /**
   * Reset the response data
   *
   * @returns {void}
   */
  const reset = () => {
    setData(null);
    setIsLoading(false);
  };

  return {
    data,
    isLoading,
    submitAnswer,
    reset
  };
};

export default useQuestionResult;
